import { ReactNode } from 'react';

import { ApprovalRequestMessage } from '../../../../../../types/ApprovalRequestMessage';
import { ApprovalRequestState } from '../../../../../../types/ApprovalRequestState';
import { OfferApprovalSettingsMessage } from '../../../../../../types/OfferApprovalSettingsMessage';
import { Button } from '../../../../../cdl/Button/Button';
import { Tooltip } from '../../../../../cdl/Tooltip/Tooltip';
import { translate } from '../../../../../common/helpers/translate.helper';
import { OrderButton } from '../../../components/OfferCard/components/OrderButton';
import { useOfferCardContext } from '../../../components/OfferCard/hooks/useOfferCardContext';

interface RequestApprovalButtonProps {
    onClick: () => void;
    disabled?: boolean;
    children: ReactNode;
}

const RequestApprovalButton = ({ onClick, disabled = false, children }: RequestApprovalButtonProps) => {
    if (!disabled) {
        return (
            <Button emphasis="high" width="100%" onClick={onClick}>
                {children}
            </Button>
        );
    }

    return (
        <Tooltip label={translate('approvalRequest.buttons.orderNowDisabledTooltip')}>
            <Button emphasis="high" width="100%" onClick={onClick} disabled={disabled}>
                {children}
            </Button>
        </Tooltip>
    );
};

interface RequestButtonProps {
    approvalRequests: ApprovalRequestMessage[];
    offerApprovalSettings: OfferApprovalSettingsMessage;
    onRequestApprovalButtonClicked: () => void;
}

export const RequestButton = (props: RequestButtonProps) => {
    const { offer } = useOfferCardContext();
    const showDisabledAction = offer.isUnquoted() && !offer.vessel.directOrderAllowed;
    const requiresApproval = props.offerApprovalSettings.requiresApproval;
    const latestApprovalRequestState = props.approvalRequests[0]?.state;

    if (!requiresApproval) {
        return <OrderButton disabled={showDisabledAction} />;
    }

    switch (latestApprovalRequestState) {
        case undefined:
        case ApprovalRequestState.DECLINED:
        case ApprovalRequestState.WITHDRAWN:
        case ApprovalRequestState.EXPIRED:
            return (
                <RequestApprovalButton onClick={props.onRequestApprovalButtonClicked} disabled={showDisabledAction}>
                    {translate('approvalRequest.buttons.requestApproval')}
                </RequestApprovalButton>
            );
        case ApprovalRequestState.PENDING:
            return (
                <RequestApprovalButton onClick={props.onRequestApprovalButtonClicked} disabled={showDisabledAction}>
                    {translate('approvalRequest.buttons.sendRequestAgain')}
                </RequestApprovalButton>
            );
        case ApprovalRequestState.APPROVED:
            return <OrderButton disabled={showDisabledAction} />;
    }
};
